// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

// material ui
import { IconButton, Stack, Tooltip } from '@mui/material';

// api
import { useGetEforsyningIntegrationByIdQuery } from '@/api/recalc/endpoints/eforsyningIntegrationsApiSlice';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import DeleteEforsyningIntegrationDialog from '@/features/eforsyning-integration/components/DeleteEforsyningIntegrationDialog';
import EforsyningIntegrationDetailHeader from '@/features/eforsyning-integration/components/EforsyningIntegrationDetailHeader';

export const Route = createFileRoute(
  '/$accountId/_user/eforsyning-integration/$integrationId/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('eforsyning-integration');
  const { integrationId } = useParams({ strict: false });
  const navigate = useNavigate();

  const {
    data: integration,
    isLoading,
    isFetching,
  } = useGetEforsyningIntegrationByIdQuery(integrationId, {
    skip: !integrationId,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleted = () => {
    navigate({ to: '../' });
  };

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="eforsyning-integration-detail-container"
    >
      <PageHeader
        title={t('detailTitle', {
          defaultValue: 'Integration Details',
        })}
      >
        <WritableOnly>
          <Tooltip
            title={t('edit', {
              defaultValue: 'Edit integration',
            })}
          >
            <IconButton
              component={Link}
              to="edit"
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: 2,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <EditRoundedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t('delete', {
              defaultValue: 'Delete integration',
            })}
          >
            <IconButton
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                color: 'text.secondary',
                borderRadius: 2,
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Tooltip>
        </WritableOnly>
      </PageHeader>

      <GatewayLoadingIndicator isLoading={isLoading || isFetching} />

      <Stack spacing={2} mb={2}>
        <EforsyningIntegrationDetailHeader integration={integration} />
      </Stack>

      <DeleteEforsyningIntegrationDialog
        open={deleteDialogOpen}
        integration={integration ?? null}
        onClose={handleDeleteClose}
        onDeleted={handleDeleted}
      />
    </PageContainer>
  );
}
